import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  EmpresaDocumento,
  EmpresaListItem,
  EmpresaMatchingConfig,
  EmpresaMatchingConfigInput,
  EmpresaPerfil,
} from '@/types/empresa';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useEmpresa(id: string | undefined) {
  return useQuery<EmpresaPerfil>({
    queryKey: ['empresa', id],
    queryFn: async () => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.get<ApiResponse<EmpresaPerfil>>(`/empresas/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
  });
}

export function useEmpresaDocumentos(id: string | undefined) {
  return useQuery<EmpresaDocumento[]>({
    queryKey: ['empresa-documentos', id],
    queryFn: async () => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.get<ApiResponse<EmpresaDocumento[]>>(`/empresas/${id}/documentos`);
      return response.data;
    },
    enabled: Boolean(id),
  });
}

/**
 * Envia um documento autorizado da empresa e atualiza a lista usada pelos
 * agentes. O endpoint processa imediatamente PDFs/imagens quando possível,
 * para que um edital/termo recém-enviado possa ser selecionado no fluxo atual.
 */
export function useEnviarDocumentoEmpresa(id: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ arquivo, categoria, dataValidade }: { arquivo: File; categoria: string; dataValidade?: string }) => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const form = new FormData();
      form.append('arquivo', arquivo);
      form.append('categoria', categoria);
      if (dataValidade) {
        form.append('data_validade', dataValidade);
      }
      const response = await api.post<ApiResponse<EmpresaDocumento>>(`/empresas/${id}/documentos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['empresa-documentos', id] });
    },
  });
}

export function useRemoverDocumentoEmpresa(id: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (docId: number) => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      await api.delete(`/empresas/${id}/documentos/${docId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['empresa-documentos', id] });
    },
  });
}

export interface DocumentoLacuna {
  categoria: string;
  nome: string;
  descricao: string | null;
}

export interface DocumentosLacunasResponse {
  categoriasPresentes: string[];
  lacunas: DocumentoLacuna[];
}

export function useDocumentosLacunas(id: string | undefined) {
  return useQuery<DocumentosLacunasResponse>({
    queryKey: ['empresa-documentos-lacunas', id],
    queryFn: async () => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.get<ApiResponse<DocumentosLacunasResponse>>(`/empresas/${id}/documentos/lacunas`);
      return response.data;
    },
    enabled: Boolean(id),
  });
}

export function useEmpresas() {
  return useQuery<EmpresaListItem[]>({
    queryKey: ['empresas'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<EmpresaListItem[]>>('/empresas');
      return response.data;
    },
  });
}

export interface EmpresaCadastroInput {
  empresa: {
    cnpj: string;
    razao_social: string;
    nome_fantasia?: string;
    inscricao_estadual?: string;
    inscricao_municipal?: string;
    data_fundacao?: string;
    porte?: string;
    cnae_principal?: string;
    cnaes_secundarios?: string;
    email?: string;
    telefones?: string;
    site?: string;
    endereco?: string;
    responsavel_tecnico?: string;
    responsavel_comercial?: string;
    responsavel_juridico?: string;
  };
}

export interface UsuarioPortalApi {
  id: number;
  empresaId: number;
  nome: string;
  email: string;
  papel: 'owner' | 'colaborador';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type UsuarioPortalPayload = {
  nome?: string;
  email?: string;
  password?: string;
  papel?: 'owner' | 'colaborador';
  isActive?: boolean;
};

export function useCriarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EmpresaCadastroInput) => {
      const response = await api.post<ApiResponse<EmpresaPerfil>>('/empresas', payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresas'] }),
  });
}

export function useAtualizarEmpresaCadastro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: EmpresaCadastroInput }) => {
      const response = await api.put<ApiResponse<EmpresaPerfil>>(`/empresas/${id}`, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['empresas'] });
      queryClient.invalidateQueries({ queryKey: ['empresa', String(variables.id)] });
    },
  });
}

export function useRemoverEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/empresas/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresas'] }),
  });
}

export function useUsuariosPortalEmpresa(empresaId: number | null) {
  return useQuery<UsuarioPortalApi[]>({
    queryKey: ['empresa-usuarios-portal', empresaId],
    queryFn: async () => {
      if (!empresaId) return [];
      const response = await api.get<ApiResponse<{ users: UsuarioPortalApi[] }>>(`/empresas/${empresaId}/usuarios-portal`);
      return response.data.users;
    },
    enabled: empresaId !== null,
  });
}

export function useCriarUsuarioPortalEmpresa(empresaId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Required<Pick<UsuarioPortalPayload, 'nome' | 'email' | 'password'>> & Pick<UsuarioPortalPayload, 'papel'>) => {
      if (!empresaId) throw new Error('Selecione uma empresa');
      const response = await api.post<ApiResponse<{ user: UsuarioPortalApi }>>(`/empresas/${empresaId}/usuarios-portal`, payload);
      return response.data.user;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresa-usuarios-portal', empresaId] }),
  });
}

export function useAtualizarUsuarioPortalEmpresa(empresaId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UsuarioPortalPayload & { id: number }) => {
      if (!empresaId) throw new Error('Selecione uma empresa');
      const response = await api.patch<ApiResponse<{ user: UsuarioPortalApi }>>(`/empresas/${empresaId}/usuarios-portal/${id}`, payload);
      return response.data.user;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresa-usuarios-portal', empresaId] }),
  });
}

export function useRemoverUsuarioPortalEmpresa(empresaId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      if (!empresaId) throw new Error('Selecione uma empresa');
      return api.delete(`/empresas/${empresaId}/usuarios-portal/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresa-usuarios-portal', empresaId] }),
  });
}

export function useMatchingConfig(id: string | undefined) {
  return useQuery<EmpresaMatchingConfig>({
    queryKey: ['empresa-matching-config', id],
    queryFn: async () => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.get<ApiResponse<EmpresaMatchingConfig>>(`/empresas/${id}/matching-config`);
      return response.data;
    },
    enabled: Boolean(id),
  });
}

export function useAtualizarConsultorEmpresa(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (consultorId: number | null) => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.patch<ApiResponse<{ id: number; consultorId: number | null }>>(
        `/empresas/${id}/consultor`,
        { consultorId }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<EmpresaPerfil | undefined>(['empresa', id], (prev) =>
        prev ? { ...prev, consultor_id: data.consultorId } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['empresas'] });
    },
  });
}

export function useAtualizarMatchingConfig(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EmpresaMatchingConfigInput) => {
      if (!id) throw new Error('ID da empresa é obrigatório');
      const response = await api.patch<ApiResponse<EmpresaMatchingConfig>>(`/empresas/${id}/matching-config`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['empresa-matching-config', id], data);
    },
  });
}
